import { inject, Injectable, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { SentryService } from './sentry.service';
import { ConfigService } from './config.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import loadjs from 'loadjs';

interface IQueueItem {
  readonly action: string;
  readonly subject: Subject<string>;
  readonly cData?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CloudflareTurnstileService {
  private readonly sentryService: SentryService = inject(SentryService);
  private readonly configService: ConfigService = inject(ConfigService);
  private readonly nzMessageService: NzMessageService =
    inject(NzMessageService);
  private readonly document: Document = inject<Document>(DOCUMENT);
  private readonly localeId: string = inject(LOCALE_ID);

  private readonly queue: IQueueItem[] = [];

  public constructor() {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) {
      return;
    }

    window.onCloudflareTurnstileLoaded = (): void => {
      while (this.queue.length) {
        const queueItem: IQueueItem = this.queue.pop()!;
        this.run(queueItem.action, queueItem.subject);
      }
    };

    this.loadCloudflareTurnstileScript();
  }

  public execute(action: string, cData?: string): Observable<string> {
    const subject: Subject<string> = new Subject();
    if (!window.turnstile?.render) {
      this.queue.push({ action, subject, cData });
    } else {
      this.run(action, subject, cData);
    }
    return subject.asObservable();
  }

  private run(action: string, subject: Subject<string>, cData?: string): void {
    window.turnstile.render(this.document.body, {
      action,
      cData,
      language: this.localeId,
      'feedback-enabled': false,
      sitekey: this.configService.cloudflareTurnstileSiteKey,
      callback: (token: string): void => {
        subject.next(token);
        subject.complete();
      },
      'error-callback': (error: string): void => {
        subject.error(error);
      },
    });
  }

  private loadCloudflareTurnstileScript(): void {
    if (this.document.getElementById('cloudflare-turnstile')) {
      return;
    }

    const url: URL = new URL(
      'https://challenges.cloudflare.com/turnstile/v0/api.js'
    );
    url.searchParams.set('render', 'explicit');
    url.searchParams.set('onload', 'onCloudflareTurnstileLoaded');
    loadjs(url.href, {
      async: true,
      numRetries: this.configService.retryCount,
      error: (): void => {
        this.nzMessageService.error(
          $localize`:@@cloudflare-turnstile-script-error:An error occurred during loading Cloudflare Turnstile script`
        );
        console.error(
          'An error occurred during loading Cloudflare Turnstile script'
        );
        this.sentryService.captureException({
          error: 'An error occurred during loading Cloudflare Turnstile script',
          severityLevel: 'error',
          transactionName: 'LOAD_CLOUDFLARE_TURNSTILE_SCRIPT_ERROR',
        });
      },
      before: (path: string, scriptEl: HTMLElement): boolean => {
        const htmlElement: HTMLElement | null = this.document.getElementById(
          'cloudflare-turnstile'
        );
        if (htmlElement) {
          htmlElement.parentElement?.removeChild(htmlElement);
        }
        scriptEl.id = 'cloudflare-turnstile';
        return true;
      },
    });
  }
}
